import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { isSuperuser, hasPermission } from "@/lib/permissions";
import { toast } from "sonner";
import {
  Plug, CheckCircle2, AlertTriangle, Save, Plug2, Trash2,
  Upload, Download, Loader2, ShieldCheck, Activity, ExternalLink, KeyRound, Radar, Rss, RefreshCw, Radio,
} from "lucide-react";

const STATUS = {
  ok: { cls: "bg-emerald-100 text-emerald-700", label: "Connected", Icon: CheckCircle2 },
  mock: { cls: "bg-amber-100 text-amber-700", label: "Mock mode", Icon: ShieldCheck },
  error: { cls: "bg-red-100 text-red-700", label: "Test failed", Icon: AlertTriangle },
  none: { cls: "bg-gray-200 text-gray-500", label: "Not connected", Icon: Plug },
};

function statusKey(conn) {
  if (!conn || (!conn.verified && !conn.last_test_status && Object.values(conn.secrets_set || {}).every((v) => !v))) return "none";
  if (conn.mode === "mock") return "mock";
  if (conn.verified) return "ok";
  if (conn.last_test_status === "error") return "error";
  return "none";
}

function ProviderCard({ provider, conn, stats, canManage, canSimulate, onChange }) {
  const pid = provider.provider;
  const [config, setConfig] = useState(conn?.config || {});
  const [secrets, setSecrets] = useState({});
  const [mode, setMode] = useState(conn?.mode || "mock");
  const [busy, setBusy] = useState(null);
  const [pulled, setPulled] = useState(null);

  useEffect(() => {
    setConfig(conn?.config || {});
    setMode(conn?.mode || "mock");
  }, [conn]);

  const sk = statusKey(conn);
  const S = STATUS[sk];

  const run = async (fn, key) => {
    setBusy(key);
    try { await fn(); } finally { setBusy(null); }
  };

  const save = () => run(async () => {
    const merged = { ...config };
    Object.entries(secrets).forEach(([k, v]) => { if (v) merged[k] = v; });
    try {
      await api.put(`/integrations/connections/${pid}`, { config: merged, mode });
      toast.success(`${provider.name} saved`);
      setSecrets({});
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, "save");

  const test = () => run(async () => {
    try {
      const { data } = await api.post(`/integrations/connections/${pid}/test`);
      toast[data.ok ? "success" : "error"](data.message);
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, "test");

  const push = () => run(async () => {
    try {
      const { data } = await api.post(`/integrations/connections/${pid}/push`, {
        title: "ERIO remediation action — qualify alternate supplier",
        description: "Auto-pushed from ERIO to validate the connection flow.",
      });
      toast.success(`${data.message} (${data.external_id})`);
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, "push");

  const pull = () => run(async () => {
    try {
      const { data } = await api.post(`/integrations/connections/${pid}/pull`, { limit: 5 });
      setPulled(data.items || []);
      toast.success(`Pulled ${data.items?.length || 0} record(s) [${data.mode}]`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, "pull");

  const remove = () => run(async () => {
    try { await api.delete(`/integrations/connections/${pid}`); toast.success("Disconnected"); setPulled(null); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, "remove");

  // --- Connector simulation (premium feeds + operational connectors) -------
  const OPERATIONAL = ["servicenow", "jira", "ariba"];
  const isSimable = provider.premium || OPERATIONAL.includes(pid);
  const [auto, setAuto] = useState(() => {
    try { return localStorage.getItem(`erio_autosim_${pid}`) === "1"; } catch { return false; }
  });

  const simulate = (count = 5) => run(async () => {
    try {
      const { data } = await api.post(`/integrations/connections/${pid}/simulate`, { count });
      toast.success(data.message || "Connector delivered");
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, "simulate");

  useEffect(() => {
    if (!auto || !isSimable || mode !== "mock") return;
    const t = setInterval(() => {
      api.post(`/integrations/connections/${pid}/simulate`, { count: 3 })
        .then(() => onChange()).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [auto, isSimable, mode, pid]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAuto = () => {
    setAuto((v) => {
      const nv = !v;
      try { localStorage.setItem(`erio_autosim_${pid}`, nv ? "1" : "0"); } catch { /* ignore */ }
      toast[nv ? "success" : "message"](nv
        ? `Auto-simulation on — ${provider.name} delivers a fresh batch every 20s`
        : `Auto-simulation off for ${provider.name}`);
      return nv;
    });
  };

  return (
    <section className="rounded-md border border-black/5 bg-white p-6" data-testid={`integration-card-${pid}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-semibold text-ek-ink">{provider.name}</h3>
            <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${S.cls}`} data-testid={`integration-status-${pid}`}>
              <S.Icon size={11} /> {S.label}
            </span>
            {provider.premium && (
              <span className="flex items-center gap-1 rounded-full bg-ek-purple/10 px-2 py-0.5 text-[10px] font-bold uppercase text-ek-purple" data-testid={`integration-licence-${pid}`}>
                <KeyRound size={11} /> Licence required
              </span>
            )}
          </div>
          <p className="mt-1 max-w-xl text-sm text-ek-ink/65">{provider.description}</p>
          <p className="mt-1 text-xs text-ek-ink/35">{provider.category} · {provider.auth}</p>
          {provider.premium && provider.licence_note && (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-xs text-amber-700" data-testid={`integration-licence-note-${pid}`}>
              <KeyRound size={12} /> {provider.licence_note}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-ek-canvas px-3 py-1.5 text-xs text-ek-ink/65" data-testid={`integration-activity-stat-${pid}`}>
          <Activity size={12} className="text-ek-purple" />
          {stats?.count
            ? <span>{stats.count} {stats.count === 1 ? "activity" : "activities"}{stats.last ? ` · last ${new Date(stats.last).toLocaleDateString()}` : ""}</span>
            : <span className="text-ek-ink/35">No activity yet</span>}
        </div>
      </div>

      {canManage ? (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {provider.fields.map((f) => (
              <label key={f.key} className="block">
                <span className="text-xs font-medium text-ek-ink/60">{f.label}</span>
                <input
                  type={f.secret ? "password" : "text"}
                  value={f.secret ? (secrets[f.key] ?? "") : (config[f.key] ?? "")}
                  onChange={(e) => f.secret
                    ? setSecrets((s) => ({ ...s, [f.key]: e.target.value }))
                    : setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                  placeholder={f.secret && conn?.secrets_set?.[f.key] ? "•••••••• (saved — leave blank to keep)" : f.placeholder}
                  data-testid={`integration-${pid}-field-${f.key}`}
                  className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                />
              </label>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-md border border-black/10 p-1 text-xs" data-testid={`integration-${pid}-mode`}>
              {["mock", "live"].map((m) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`integration-${pid}-mode-${m}`}
                  className={`rounded px-3 py-1 font-medium capitalize ${mode === m ? "bg-ek-purple text-white" : "text-ek-ink/65"}`}>
                  {m === "mock" ? "Mock / demo" : "Live"}
                </button>
              ))}
            </div>
            <button onClick={save} disabled={busy} className="ek-btn flex items-center gap-1.5" data-testid={`integration-${pid}-save`}>
              {busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </button>
            <button onClick={test} disabled={busy} className="ek-btn-outline flex items-center gap-1.5" data-testid={`integration-${pid}-test`}>
              {busy === "test" ? <Loader2 size={15} className="animate-spin" /> : <Plug2 size={15} />} Test connection
            </button>
            {provider.supports?.push && (
              <button onClick={push} disabled={busy} className="ek-btn-outline flex items-center gap-1.5" data-testid={`integration-${pid}-push`}>
                {busy === "push" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {provider.push_label}
              </button>
            )}
            {provider.supports?.pull && (
              <button onClick={pull} disabled={busy} className="ek-btn-outline flex items-center gap-1.5" data-testid={`integration-${pid}-pull`}>
                {busy === "pull" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {provider.pull_label}
              </button>
            )}
            <button onClick={remove} disabled={busy} className="ek-btn-outline ml-auto flex items-center gap-1.5 text-red-500" data-testid={`integration-${pid}-delete`}>
              <Trash2 size={14} /> Disconnect
            </button>
          </div>
        </>
      ) : (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-black/10 bg-ek-canvas px-3 py-2.5 text-xs text-ek-ink/65" data-testid={`integration-${pid}-readonly`}>
          <ShieldCheck size={13} className="text-ek-purple" />
          Connection status is read-only. API credentials and connection settings are managed by the system Owner.
        </div>
      )}

      {conn?.last_test_status === "error" && conn?.last_test_error && (
        <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-600" data-testid={`integration-${pid}-error`}>{conn.last_test_error}</p>
      )}

      {isSimable && canSimulate && mode === "mock" && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-ek-purple/15 bg-ek-purple/[0.04] p-3" data-testid={`integration-${pid}-sim-panel`}>
          <Radio size={15} className="shrink-0 text-ek-purple" />
          <span className="min-w-0 text-xs text-ek-ink/60">
            {provider.premium
              ? "Demo this feed without a licence — generates intelligence against your suppliers (Signals + activity + supplier enrichment)."
              : "Simulate inbound operational data — incidents, completed remediation tasks (maturing controls) and supplier-exposure refreshes, with a closed-loop rescore that auto-raises a risk and lights the alert bell on a critical event."}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => simulate(5)} disabled={busy === "simulate"} className="ek-btn flex items-center gap-1.5 !py-1.5 text-xs" data-testid={`integration-${pid}-simulate`}>
              {busy === "simulate" ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />} {provider.premium ? "Simulate inbound feed" : "Simulate inbound data"}
            </button>
            <button
              onClick={toggleAuto}
              data-testid={`integration-${pid}-autosim`}
              title="Drip in a fresh batch every 20s"
              className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${auto ? "border-ek-purple bg-ek-purple/10 text-ek-purple" : "border-black/10 text-ek-ink/65 hover:text-ek-ink"}`}
            >
              <span className={`h-2 w-2 rounded-full ${auto ? "animate-pulse bg-ek-purple" : "bg-ek-ink/30"}`} /> Auto {auto ? "on" : "off"}
            </button>
          </div>
        </div>
      )}

      {pulled && (
        <div className="mt-4 rounded-md border border-black/5 bg-ek-canvas p-3" data-testid={`integration-${pid}-pull-result`}>
          <p className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">Pulled records</p>
          <div className="mt-2 space-y-1">
            {pulled.length === 0 ? <p className="text-sm text-ek-ink/65">No records.</p> :
              pulled.map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-ek-ink/70">
                  <span className="font-mono text-xs text-ek-purple">{it.external_id}</span>
                  <span className="truncate">{it.title}</span>
                  {it.status != null && <span className="ml-auto text-xs text-ek-ink/65">{it.status}</span>}
                  {it.overall_risk != null && <span className="ml-auto text-xs text-amber-600">risk {Math.round(it.overall_risk * 100)}%</span>}
                </div>
              ))}
          </div>
        </div>
      )}
    </section>
  );
}

const FREE_FEED_META = {
  cve: {
    name: "CVE / NVD vulnerabilities",
    category: "Cyber",
    description: "Recent high & critical software vulnerabilities (CIRCL CVE Search) normalised into your signal store.",
  },
  gdacs: {
    name: "GDACS disaster alerts",
    category: "Operational resilience",
    description: "Live earthquake, cyclone, flood and wildfire alerts (Global Disaster Alert & Coordination System).",
  },
  sanctions_lists: {
    name: "Sanctions-list freshness",
    category: "Compliance",
    description: "Update freshness & entity counts for OFAC, OFSI/FCDO, EU and UN consolidated lists (OpenSanctions).",
  },
};

function FreeFeedsSection({ status, canTrigger, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!status || !status.available?.length) return null;

  const trigger = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/feeds/ingest");
      toast.success(`Refreshed free feeds — ${data.ingested} new signal(s)`);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-12" data-testid="free-feeds-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="ek-eyebrow flex items-center gap-1.5"><Rss size={13} /> Free intelligence feeds</h2>
          <p className="mt-1 max-w-2xl text-sm text-ek-ink/65">
            Keyless public risk feeds ingested automatically every 12 hours — no licence required. They run
            <span className="font-medium text-emerald-600"> live</span> out of the box.
          </p>
        </div>
        {canTrigger && (
          <button onClick={trigger} disabled={busy} className="ek-btn-outline flex items-center gap-1.5" data-testid="free-feeds-refresh">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Refresh now
          </button>
        )}
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {status.available.map((fid) => {
          const meta = FREE_FEED_META[fid] || { name: fid, category: "Feed", description: "" };
          const run = status.feeds?.[fid] || {};
          const live = !run.error;
          return (
            <div key={fid} className="rounded-md border border-black/5 bg-white p-5" data-testid={`free-feed-${fid}`}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-base font-semibold text-ek-ink">{meta.name}</h3>
                <span
                  className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${live ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                  data-testid={`free-feed-status-${fid}`}
                >
                  {live ? <><CheckCircle2 size={11} /> Live</> : <><AlertTriangle size={11} /> Retrying</>}
                </span>
              </div>
              <p className="mt-2 text-xs text-ek-ink/65">{meta.description}</p>
              <div className="mt-3 flex items-center gap-3 text-[11px] text-ek-ink/65">
                <span className="rounded-full bg-ek-canvas px-2 py-0.5 font-medium">{meta.category}</span>
                {run.last_run && <span>Updated {new Date(run.last_run).toLocaleDateString()}</span>}
                {run.fetched != null && <span>· {run.fetched} fetched</span>}
              </div>
              {run.error && <p className="mt-2 text-[11px] text-amber-600">{run.error}</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
}


export default function Integrations() {
  const { user } = useAuth();
  const canManage = isSuperuser(user);
  const canSimulate = hasPermission(user, "integration.manage");
  const [providers, setProviders] = useState([]);
  const [connByProvider, setConnByProvider] = useState({});
  const [activity, setActivity] = useState([]);
  const [freeFeeds, setFreeFeeds] = useState(null);

  const activityByProvider = useMemo(() => {
    const m = {};
    activity.forEach((a) => {
      const p = a.provider;
      if (!m[p]) m[p] = { count: 0, last: null };
      m[p].count += 1;
      if (a.created_at && (!m[p].last || a.created_at > m[p].last)) m[p].last = a.created_at;
    });
    return m;
  }, [activity]);

  const load = useCallback(() => {
    api.get("/integrations/providers").then(({ data }) => setProviders(data.providers)).catch(() => {});
    api.get("/integrations/connections").then(({ data }) => {
      const map = {};
      (data.items || []).forEach((c) => { map[c.provider] = c; });
      setConnByProvider(map);
    }).catch(() => {});
    api.get("/integrations/activity").then(({ data }) => setActivity(data.items || [])).catch(() => {});
    api.get("/feeds/status").then(({ data }) => setFreeFeeds(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Enterprise Integrations" description="Connect Jira, ServiceNow and SAP Ariba to ERIO." path="/integrations" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="integrations-page">
        <h1 className="ek-eyebrow">Connectors</h1>
        <h2 className="mt-1 font-display text-3xl font-bold text-ek-ink">Enterprise Integrations</h2>
        <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
          Connect your enterprise instances to push ERIO risk &amp; remediation actions out, and pull incidents,
          CMDB and supplier-risk signals in. Credentials are encrypted at rest (per-tenant). Start in
          <span className="font-medium text-ek-ink/70"> Mock / demo</span> mode to validate the flow, then switch to
          <span className="font-medium text-ek-ink/70"> Live</span> once your keys pass a test connection.
        </p>

        <div className="mt-8 space-y-6">
          {providers.filter((p) => !p.premium).map((p) => (
            <ProviderCard key={p.provider} provider={p} conn={connByProvider[p.provider]} stats={activityByProvider[p.provider]} canManage={canManage} canSimulate={canSimulate} onChange={load} />
          ))}
        </div>

        {providers.some((p) => p.premium) && (
          <section className="mt-12" data-testid="integration-feeds-section">
            <h2 className="ek-eyebrow flex items-center gap-1.5"><Radar size={13} /> Intelligence feeds</h2>
            <p className="mt-1 max-w-2xl text-sm text-ek-ink/65">
              Premium external risk intelligence that enriches your third-party register — financial distress,
              cyber ratings, ESG and beneficial ownership. Each is built and demoable in
              <span className="font-medium text-ek-ink/70"> Mock</span> mode today, and flips to
              <span className="font-medium text-ek-ink/70"> Live</span> the moment you add the provider's licence key
              and pass a test connection.
            </p>
            <div className="mt-5 space-y-6">
              {providers.filter((p) => p.premium).map((p) => (
                <ProviderCard key={p.provider} provider={p} conn={connByProvider[p.provider]} stats={activityByProvider[p.provider]} canManage={canManage} canSimulate={canSimulate} onChange={load} />
              ))}
            </div>
          </section>
        )}

        <FreeFeedsSection status={freeFeeds} canTrigger={canManage} onChange={load} />

        <section className="mt-10" data-testid="integration-activity">
          <h2 className="ek-eyebrow flex items-center gap-1.5"><Activity size={13} /> Recent activity</h2>
          <p className="mt-1 text-sm text-ek-ink/65">
            Records ERIO pushed to your connected tools — manually and automatically when a remediation completes.
          </p>
          <div className="mt-4 overflow-hidden rounded-md border border-black/5 bg-white">
            {activity.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-ek-ink/65" data-testid="integration-activity-empty">
                No activity yet. Push a record, or complete a remediation plan, to see it here.
              </p>
            ) : (
              <ul className="divide-y divide-black/5">
                {activity.map((a) => {
                  const name = providers.find((p) => p.provider === a.provider)?.name || a.provider;
                  return (
                    <li key={a.activity_id} className="flex items-center gap-3 px-5 py-3" data-testid={`activity-${a.activity_id}`}>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${a.source === "remediation" ? "bg-ek-purple/10 text-ek-purple" : "bg-ek-ink/5 text-ek-ink/65"}`}>
                        {a.source === "remediation" ? "Auto" : "Manual"}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-ek-ink/80">{a.title}</p>
                        <p className="text-xs text-ek-ink/65">
                          {name}{a.mode === "mock" ? " · mock" : ""} · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
                        </p>
                      </div>
                      {a.external_id && (
                        a.url ? (
                          <a href={a.url} target="_blank" rel="noreferrer" className="flex shrink-0 items-center gap-1 font-mono text-xs text-ek-purple hover:underline" data-testid={`activity-link-${a.activity_id}`}>
                            {a.external_id} <ExternalLink size={11} />
                          </a>
                        ) : (
                          <span className="shrink-0 font-mono text-xs text-ek-ink/65">{a.external_id}</span>
                        )
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
